import { Injectable, NgZone } from "@angular/core";
import { Observable } from "rxjs";
import { WalletSseParser } from "../../parsers/wallet-sse.parser";
import { SessionCtl } from "../../services/session.ctl.service";

export interface WalletOperationsSseOptions {
  readonly url: string;
  readonly reconnectInitialDelayMs?: number;
  readonly reconnectMaxDelayMs?: number;
}

@Injectable({ providedIn: "root" })
export class WalletOperationsSseClient {
  constructor(
    private readonly sessionCtl: SessionCtl,
    private readonly zone: NgZone,
  ) {}

  connect<T>(
    options: WalletOperationsSseOptions,
    parse: (data: string) => T | null,
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      let stopped = false;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let controller: AbortController | null = null;
      let reconnectAttempt = 0;

      const scheduleReconnect = (): void => {
        if (stopped) return;

        const initialDelay = options.reconnectInitialDelayMs ?? 1000;
        const maxDelay = options.reconnectMaxDelayMs ?? 30000;
        const delay = Math.min(initialDelay * 2 ** reconnectAttempt, maxDelay);
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          void openStream();
        }, delay);
      };

      const readStream = async (
        response: Response,
        signal: AbortSignal,
      ): Promise<void> => {
        if (!response.body) {
          throw new Error("El stream SSE de Wallet Operations no contiene un body legible.");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const parser = new WalletSseParser();

        try {
          while (!stopped && !signal.aborted) {
            const { done, value } = await reader.read();
            if (done) return;

            for (const event of parser.push(decoder.decode(value, { stream: true }))) {
              const parsed = parse(event.data);
              if (parsed !== null) {
                this.zone.run(() => subscriber.next(parsed));
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      };

      const openStream = async (): Promise<void> => {
        if (stopped) return;

        controller = new AbortController();
        try {
          const response = await fetch(options.url, {
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
            },
            signal: controller.signal,
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          reconnectAttempt = 0;
          await readStream(response, controller.signal);
          if (!stopped && !controller.signal.aborted) scheduleReconnect();
        } catch (error) {
          if (
            !stopped &&
            !(error instanceof DOMException && error.name === "AbortError")
          ) {
            scheduleReconnect();
          }
        }
      };

      void openStream();

      return () => {
        stopped = true;
        controller?.abort();
        if (reconnectTimer) clearTimeout(reconnectTimer);
      };
    });
  }
}
